import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Snackbar,
  Typography,
  Button,
} from "@mui/material";
import {
  Star,
  StarBorder,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import MuiAlert from "@mui/material/Alert";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { useUser } from "../context/UserContext";
import "./Data.css";
import gradientBg from "../assets/gradient-bg.jpg";

const Data = () => {
  const { user } = useUser();

  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState("all");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Full-page view states
  const [viewOpen, setViewOpen] = useState(false);
  const [currentUrl, setCurrentUrl] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");
  const [fullscreen, setFullscreen] = useState(false);

  // Fetch data by organization
  useEffect(() => {
    if (!user?.organizationId) return;

    const q = query(
      collection(db, "data"),
      where("organization", "==", user.organizationId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dataDocs = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        sno: index + 1,
        ...doc.data(),
      }));
      setData(dataDocs);
    });

    return () => unsubscribe();
  }, [user]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleFavoriteToggle = async (id, current) => {
    await updateDoc(doc(db, "data", id), { favorite: !current });
    showSnackbar("Favorite status updated", "info");
  };

  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => showSnackbar("Link copied!", "success"));
  };

  const filteredData = data
    .filter((item) =>
      item.title?.toLowerCase().includes(searchText.toLowerCase())
    )
    .filter((item) => (filter === "favorites" ? item.favorite : true))
    .map((item, index) => ({ ...item, sno: index + 1 }));

  const columns = [
    { field: "title", headerName: "Data Name", flex: 1 },
    {
      field: "favorite",
      headerName: "Favorite",
      width: 100,
      renderCell: (params) => (
        <Tooltip
          title={params.row.favorite ? "Unmark Favorite" : "Mark Favorite"}
        >
          <IconButton
            onClick={() =>
              handleFavoriteToggle(params.row.id, params.row.favorite)
            }
            sx={{ "&:hover": { backgroundColor: "transparent" } }}
          >
            {params.row.favorite ? (
              <Star sx={{ color: "#ad1010" }} />
            ) : (
              <StarBorder sx={{ color: "#ad1010" }} />
            )}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => (
        <Box display="flex" gap={1} mt={1}>
          <Button
            size="small"
            variant="contained"
            startIcon={<VisibilityIcon />}
            sx={{
              textTransform: "none",
              paddingBottom: "1px",
              backgroundColor: "transparent",
              color: "#645792",
              border: "1px solid #645792",
              "&:hover": {
                backgroundColor: "#645792",
                color: "#fff",
              },
            }}
            onClick={() => {
              if (params.row.viewLink) {
                setCurrentUrl(params.row.viewLink);
                setCurrentTitle(params.row.title);
                setViewOpen(true);
                setFullscreen(false);
              } else {
                showSnackbar("No URL available", "warning");
              }
            }}
          >
            View
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<ContentCopyIcon />}
            sx={{
              textTransform: "none",
              paddingBottom: "1px",
              backgroundColor: "transparent",
              color: "#645792",
              border: "1px solid #645792",
              "&:hover": {
                backgroundColor: "#645792",
                color: "#fff",
              },
            }}
            onClick={() => handleCopyLink(params.row.viewLink)}
          >
            Copy Link
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box p={4} sx={{ width: "100%", overflowX: "auto", position: "relative" }}>
      <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto" }}>
        {/* Search + Filter */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mb={2}
        >
          <TextField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search data"
            size="small"
            sx={{ width: 200, backgroundColor: "rgba(255,255,255,0.3)" }}
          />
          <Tooltip
            title={filter === "favorites" ? "Show All" : "Show Favorites"}
            disableInteractive
          >
            <IconButton
              onClick={() =>
                setFilter(filter === "favorites" ? "all" : "favorites")
              }
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
            >
              {filter === "favorites" ? (
                <Star sx={{ color: "#ad1010" }} />
              ) : (
                <StarBorder sx={{ color: "#ad1010" }} />
              )}
              <Typography sx={{ fontSize: "1rem", ml: 0.5 }}>
                Favorite
              </Typography>
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontFamily: "Lato, sans-serif",
            color: "#645792",
            fontWeight: "bold",
          }}
        >
          Data
        </Typography>

        {/* DataGrid */}
        {!viewOpen && (
          <Box
            sx={{
              width: "100%",
              transition: "all 0.3s ease",
              overflow: "hidden",
              flexGrow: 1,
            }}
          >
            <DataGrid
              className="grid"
              rows={filteredData}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              sx={{
                mt: 2,
                borderRadius: 2,
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "rgba(180, 87, 146,0.1)",
                },
              }}
            />
          </Box>
        )}

        {viewOpen && currentUrl && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100vh", // full viewport height
              width: "100%",
              backgroundImage: `url(${gradientBg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center center",
              backgroundSize: "cover",
            }}
          >
            {/* Top bar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                p: 1,
                backgroundColor: "#645792",
                flexShrink: 0, // top bar does not shrink
                height: "50px", // fixed height
              }}
            >
              <Button
                onClick={() => setViewOpen(false)}
                sx={{
                  color: "white",
                  borderColor: "white",
                  border: "1px solid",
                }}
              >
                Close
              </Button>
            </Box>

            {/* Content: sidebar + iframe */}
            <Box
              sx={{
                display: "flex",
                flexGrow: 1, // takes remaining vertical space
                overflow: "hidden",
              }}
            >
              {/* Sidebar placeholder */}

              {/* Iframe */}
              <Box sx={{ flexGrow: 1, position: "relative" }}>
                <iframe
                  src={currentUrl}
                  title={currentTitle}
                  style={{
                    border: "none",
                    width: "100%",
                    height: "100%", // fills remaining vertical & horizontal space
                  }}
                  allowFullScreen
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <MuiAlert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Data;
